var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html, LitElement, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { localized, msg } from "@lit/localize";
import { commonCssStyles } from "../../global-styles.js";
import "../es-logo/ESLogo.js";
import "../nav-bar-link/NavBarLink.js";
import "../profile-dropdown-menu/ProfileDropdownMenu.js";
import "../mobile-menu/MobileMenu.js";
export var SelectedLink;
(function (SelectedLink) {
    SelectedLink["DASHBOARD"] = "DASHBOARD";
    SelectedLink["MY_TESTS"] = "MY_TESTS";
    SelectedLink["BILLING"] = "BILLING";
    SelectedLink["SUPPORT"] = "SUPPORT";
})(SelectedLink || (SelectedLink = {}));
export var HeaderEvent;
(function (HeaderEvent) {
    HeaderEvent["DASHBOARD_CLICKED"] = "DASHBOARD_CLICKED";
    HeaderEvent["MY_TESTS_CLICKED"] = "MY_TESTS_CLICKED";
    HeaderEvent["BILLING_CLICKED"] = "BILLING_CLICKED";
    HeaderEvent["SUPPORT_CLICKED"] = "SUPPORT_CLICKED";
    HeaderEvent["MY_PROFILE_CLICKED"] = "MY_PROFILE_CLICKED";
    HeaderEvent["LOG_OUT_CLICKED"] = "LOG_OUT_CLICKED";
})(HeaderEvent || (HeaderEvent = {}));
export function headerEventFn(eventTarget, headerEvent) {
    return () => {
        const customEvent = new CustomEvent(headerEvent, {
            bubbles: true,
            composed: true,
        });
        eventTarget.dispatchEvent(customEvent);
    };
}
/**
 * The consumer is expected to fix the header to the top of the viewport.
 * See `PatientHubLayout.ts` as an example.
 *
 * @fires CustomEvent#DASHBOARD_CLICKED
 * @fires CustomEvent#MY_TESTS_CLICKED
 * @fires CustomEvent#BILLING_CLICKED
 * @fires CustomEvent#SUPPORT_CLICKED
 * @fires CustomEvent#MY_PROFILE_CLICKED
 * @fires CustomEvent#LOG_OUT_CLICKED
 */
let ESHeader = class ESHeader extends LitElement {
    constructor() {
        super(...arguments);
        this.profileAlert = false;
        this.authenticated = false;
        this.selectedLink = undefined;
    }
    render() {
        return html `
      <div class="full-width-container">
        <div class="inner-content">
          <es-logo class="logo"></es-logo>
          ${this.authenticated
            ? html ` <nav
                  aria-label="${msg("Main")}"
                  aria-orientation="horizontal"
                >
                  <ul class="nav-wrapper">
                    <li>
                      <nav-bar-link
                        text="${msg("Dashboard")}"
                        @click="${headerEventFn(this, HeaderEvent.DASHBOARD_CLICKED)}"
                        ?selected="${this.selectedLink ==
                SelectedLink.DASHBOARD}"
                      >
                      </nav-bar-link>
                    </li>

                    <li>
                      <nav-bar-link
                        text="${msg("My tests")}"
                        @click="${headerEventFn(this, HeaderEvent.MY_TESTS_CLICKED)}"
                        ?selected="${this.selectedLink ==
                SelectedLink.MY_TESTS}"
                      >
                      </nav-bar-link>
                    </li>

                    <li>
                      <nav-bar-link
                        text="${msg("Billing")}"
                        @click="${headerEventFn(this, HeaderEvent.BILLING_CLICKED)}"
                        ?selected="${this.selectedLink == SelectedLink.BILLING}"
                      >
                      </nav-bar-link>
                    </li>

                    <li>
                      <nav-bar-link
                        text="${msg("Support")}"
                        @click="${headerEventFn(this, HeaderEvent.SUPPORT_CLICKED)}"
                        ?selected="${this.selectedLink == SelectedLink.SUPPORT}"
                      >
                      </nav-bar-link>
                    </li>
                  </ul>
                </nav>
                <profile-dropdown-menu
                  class="dropdown"
                  user-initials="${ifDefined(this.userInitials)}"
                  ?alert="${this.profileAlert}"
                ></profile-dropdown-menu>
                <mobile-menu
                  class="mobile-menu"
                  user-initials="${ifDefined(this.userInitials)}"
                  ?alert="${this.profileAlert}"
                ></mobile-menu>`
            : nothing}
        </div>
      </div>
    `;
    }
};
// noinspection CssUnresolvedCustomProperty
ESHeader.styles = [
    commonCssStyles,
    css `
      .full-width-container {
        background: var(--white-color);
        border-bottom: 1px solid var(--neutral-dark-10-color);
        z-index: 150;
        position: relative;
        box-shadow: 0 0 4px 0 rgba(115, 115, 115, 0.16);
      }

      .inner-content {
        display: flex;
        align-items: center;
        margin: 0 var(--margin-s);
        width: auto;
        max-width: var(--inner-content-max-width);
        height: 52px;
        gap: var(--spacing-2xl);
      }

      .nav-wrapper {
        display: none;
      }

      .logo {
        display: flex;
        flex-grow: 0;
        flex-shrink: 0;
        flex-basis: 80px;
        height: 28px;
      }

      .dropdown {
        display: none;
        margin-left: auto;
      }

      .mobile-menu {
        display: block;
        margin-left: auto;
      }

      @media (min-width: 768px) {
        .inner-content {
          margin: 0 var(--margin-m);
        }
      }

      @media (min-width: 1200px) {
        .inner-content {
          margin: 0 var(--margin-l);
          height: 72px;
        }

        .nav-wrapper {
          z-index: 150;
          display: flex;
          align-items: center;
          gap: var(--spacing-2xl);
          background: var(--white-color);
          box-shadow:
            -40px 0 var(--white-color),
            40px 0 var(--white-color);
          margin: 0;
          padding: 0;
        }

        .nav-wrapper li {
          display: flex;
        }

        .logo {
          flex-basis: 100px;
          height: 36px;
        }

        .dropdown {
          display: block;
        }

        .mobile-menu {
          display: none;
          width: 0;
        }
      }

      @media (min-width: 1280px) {
        .inner-content {
          margin: 0 var(--margin-xl);
        }
      }
    `,
];
__decorate([
    property({ attribute: "user-initials" })
], ESHeader.prototype, "userInitials", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "profile-alert" })
], ESHeader.prototype, "profileAlert", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: "authenticated" })
], ESHeader.prototype, "authenticated", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: "selected-link" })
], ESHeader.prototype, "selectedLink", void 0);
ESHeader = __decorate([
    customElement("es-header"),
    localized()
], ESHeader);
export { ESHeader };
